import React, { useState, useRef, useEffect } from 'react';
import { SendOutlined, RobotOutlined } from '@ant-design/icons';
import '../assets/css/home.css';
import { apiPostMessage, apiLawerInfo, apiGetHistoryMessage } from '../request/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { zeusApp } from '../request/index';


interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  type?: string;
  uscc?: string;
  userId?: string;
  appId?: string;
  token?: string;
}

const Home: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [lawyerucc, setLawyerucc] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChatStarted, setIsChatStarted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const userId = localStorage.getItem('id') || '';
  const userName = localStorage.getItem('userName') || '';
  const token = localStorage.getItem('userToken') || '';
  const appId = zeusApp || '';

  const [connectionStatus, setConnectionStatus] = useState('未连接');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialInputRef = useRef<HTMLTextAreaElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const socketRef = useRef<WebSocket | null>(null);

  // 获取用户token
  const getToken = () => {
    return localStorage.getItem('userToken') || '';
  };

  // WebSocket连接
  useEffect(() => {
    connectWebSocket();
    apiLawerInfo().then(res => {
      console.log(res)
      if (res.data.ret == '0') {
        setLawyerucc(res.data.data.uscc)
        const historyInfo = {
          uscc: lawyerucc,
          page:1,
          size:10
        }
        apiGetHistoryMessage(historyInfo).then(res => {
          console.log(res)
        })
      } else {

      }
    })
    // 组件卸载时关闭WebSocket连接
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  // 连接WebSocket（已添加认证参数）
  const connectWebSocket = () => {
    const token = getToken();
    // 检查是否有token
    if (!token) {
      console.error('用户未登录，无法连接WebSocket');
      setConnectionStatus('未登录，请先登录');
      return;
    }

    // 构建带认证参数的WebSocket URL
    // const baseUrl = 'ws://localhost:42007/zeus/api/ws/chat';
    // const baseUrl = 'wss://106.14.173.165:32201/ws/chat';
    const baseUrl = 'wss://api.zeus.ipcn.cn/ws/chat';
    const wsUrl = `${baseUrl}?zeus-token=${encodeURIComponent(token)}&zeus-app=${encodeURIComponent(zeusApp)}`;

    console.log('正在连接WebSocket:', wsUrl);

    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket连接已建立');
        setIsConnected(true);
        setConnectionStatus('已连接');
      };

      socket.onmessage = (event) => {
        try {
          const messageData = JSON.parse(event.data);
          handleWebSocketMessage(messageData);
        } catch (error) {
          console.error('解析WebSocket消息错误:', error);
        }
      };

      socket.onclose = (event) => {
        console.log('WebSocket连接已关闭', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('已断开');

        // 5秒后尝试重连
        setTimeout(() => {
          console.log('尝试重新连接...');
          connectWebSocket();
        }, 5000);
      };

      socket.onerror = (error) => {
        console.error('WebSocket错误:', error);
        setConnectionStatus('连接错误');
        setIsConnected(false);
      };

    } catch (error) {
      console.error('创建WebSocket连接失败:', error);
      setConnectionStatus('连接失败');
    }
  };

  const handleWebSocketMessage = (messageData: any) => {
    console.log('收到WebSocket消息:', messageData);

    // ===== AI 消息 =====
    if (messageData.type === 'AI' || messageData.sender === 'AI') {
      const fullText = messageData.content || messageData.text || '';
      const messageId = `ai_${Date.now()}`;

      // ✅ 关键 1：先关闭 loading（非常重要）
      setIsLoading(false);

      // ✅ 关键 2：先插入空消息
      const aiMessage: Message = {
        id: messageId,
        text: '',
        sender: 'ai',
        timestamp: new Date(),
        type: messageData.type || 'AI',
        uscc: lawyerucc
      };

      setMessages(prev => [...prev, aiMessage]);

      // ✅ 关键 3：下一帧再启动打字机（强制产生视觉差）
      requestAnimationFrame(() => {
        typeWriter(fullText, messageId, 25);
      });

      return;
    }

    // ===== SYSTEM 消息 =====
    if (messageData.type === 'SYSTEM') {
      const systemMessage: Message = {
        id: `system_${Date.now()}`,
        text: messageData.content || messageData.text || '',
        sender: 'ai',
        timestamp: new Date(),
        type: 'SYSTEM'
      };

      setMessages(prev => [...prev, systemMessage]);
      return;
    }

    // ===== ERROR =====
    if (messageData.type === 'ERROR') {
      setIsLoading(false);

      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: `系统错误：${messageData.content || messageData.text || '未知错误'}`,
        sender: 'ai',
        timestamp: new Date(),
        type: 'SYSTEM'
      };

      setMessages(prev => [...prev, errorMessage]);
    }
  };



  // 发送WebSocket消息
  const sendWebSocketMessage = (content: string) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket未连接');
      setConnectionStatus('发送失败：未连接');
      return false;
    }

    const message = {
      id: `msg_${Date.now()}`,
      content: content,
      sender: 'user',
      type: 'USER',
      timestamp: Date.now(),
      uscc: lawyerucc,
      userId: userId + '.' + userName,
      token: token,
      appId: appId
    };

    try {
      socketRef.current.send(JSON.stringify(message));
      console.log('已发送消息:', message);
      return true;
    } catch (error) {
      console.error('发送WebSocket消息失败:', error);
      return false;
    }
  };

  // 发送HTTP API消息（备用方案）
  const sendHttpMessage = async (content: string) => {
    try {
      const response = await apiPostMessage({
        content: content,
        sender: 'user',
        type: 'USER',
        timestamp: Date.now()
      });

      console.log('HTTP API响应:', response);

      // 假设API返回AI回复
      if (response.data && response.data.content) {
        const aiMessage: Message = {
          id: `ai_${Date.now()}`,
          text: response.data.content,
          sender: 'ai',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, aiMessage]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('发送HTTP消息失败:', error);
      return false;
    }
  };

  // 自动滚动到底部
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // 自动聚焦输入框
  useEffect(() => {
    if (!isChatStarted && initialInputRef.current) {
      initialInputRef.current.focus();
    } else if (isChatStarted && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isChatStarted]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);

    // 调整textarea高度
    const textarea = e.target;

    // 重置高度为auto，让浏览器计算正确的内容高度
    textarea.style.height = 'auto';

    // 计算内容高度
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = 24; // 每行高度
    const padding = 32; // 上下padding

    // 计算基于行数的高度
    const lines = Math.max(textarea.value.split('\n').length, 1);
    const calculatedHeight = Math.max(lines * lineHeight + padding, 48); // 最小高度48px（匹配CSS）

    // 取两者中的较小值，确保高度既足够显示内容又不会太大
    const newHeight = Math.min(Math.max(scrollHeight, calculatedHeight), 400);

    textarea.style.height = newHeight + 'px';

    // 如果高度达到最大值，显示滚动条
    if (newHeight >= 400) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };
  const typeWriter = (
    fullText: string,
    messageId: string,
    speed = 30
  ) => {
    let index = 0;

    const timer = setInterval(() => {
      index++;

      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, text: fullText.slice(0, index) }
            : msg
        )
      );

      if (index >= fullText.length) {
        clearInterval(timer);
        setIsLoading(false);
      }
    }, speed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputText.trim() && !isLoading) {
        handleSendMessage();
      }
    }
  };

  // 格式化时间
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText.trim();
    setInputText('');

    // 重置输入框高度
    if (initialInputRef.current) {
      initialInputRef.current.style.height = 'auto';
    }
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
    }

    // 如果是第一条消息，切换到聊天状态
    if (!isChatStarted) {
      setIsChatStarted(true);
    }
    console.log(lawyerucc)
    // 添加用户消息
    const newUserMessage: Message = {
      id: `user_${Date.now()}`,
      text: userMessage,
      sender: 'user',
      timestamp: new Date(),
      uscc: lawyerucc,
      userId: userId
    };

    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);

    // 开始加载状态
    setIsLoading(true);

    // 优先使用WebSocket发送消息
    let sendSuccess = false;
    if (isConnected) {
      sendSuccess = sendWebSocketMessage(userMessage);
    } else {
      // 如果未连接，尝试重新连接
      const token = getToken();
      if (token) {
        connectWebSocket();
        // 等待1秒后尝试发送
        setTimeout(() => {
          if (isConnected) {
            sendSuccess = sendWebSocketMessage(userMessage);
          } else {
            setConnectionStatus('发送失败：未连接');
            setIsLoading(false);
          }
        }, 1000);
      } else {
        setConnectionStatus('未登录，请先登录');
        setIsLoading(false);
      }
    }

    // 如果WebSocket发送失败，尝试HTTP API
    if (!sendSuccess) {
      console.log('WebSocket发送失败，尝试HTTP API');
      // 可以根据需要启用HTTP备用方案
      // await sendHttpMessage(userMessage);
    }
  };

  // 清空对话
  const clearChat = () => {
    if (window.confirm('确定要清空所有对话吗？')) {
      setMessages([]);
      if (messages.length > 0) {
        const systemMessage: Message = {
          id: `system_${Date.now()}`,
          text: '对话已清空，请开始新的对话！',
          sender: 'ai',
          timestamp: new Date(),
          type: 'SYSTEM',
          uscc: lawyerucc

        };
        setMessages([systemMessage]);
      }
    }
  };

  // 重新连接WebSocket
  const reconnectWebSocket = () => {
    const token = getToken();
    if (token) {
      if (socketRef.current) {
        socketRef.current.close();
      }
      setConnectionStatus('重新连接中...');
      connectWebSocket();
    } else {
      setConnectionStatus('未登录，请先登录');
    }
  };

  // 渲染初始状态（只有一个输入框）
  if (!isChatStarted) {
    return (
      <div className="initial-container">
        <div className="initial-header">
          <h1><span>您好，{localStorage.getItem('userName') || '用户'}</span></h1>
        </div>
        <div className="initial-input-container">
          <textarea
            ref={initialInputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="输入消息..."
            className="initial-chat-input"
          />
          {inputText.trim() && (
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !isConnected}
              className="initial-send-button"
              style={{
                cursor: (!isConnected || isLoading) ? 'not-allowed' : 'pointer',
                opacity: (!isConnected || isLoading) ? 0.5 : 1
              }}
            >
              <SendOutlined />
            </button>
          )}
        </div>
        <div className="initial-input-hint">
          按 Enter 发送，Shift + Enter 换行 ｜ NEXTOOK 也可能会犯错，请仔细检查。
        </div>
        <div className="connection-status" style={{
          fontSize: '12px',
          color: isConnected ? '#10a37f' : '#ff4d4f',
          marginTop: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: isConnected ? '#52c41a' : '#ff4d4f',
            marginRight: '6px'
          }} />
          <span>连接状态: {connectionStatus}</span>
        </div>
      </div>
    );
  }

  // 渲染聊天状态
  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-title">
          <RobotOutlined style={{ color: '#10a37f', marginRight: '10px' }} />
          <h1>AI助手</h1>
          <div className="connection-indicator" style={{
            marginLeft: '20px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isConnected ? '#52c41a' : '#ff4d4f',
              marginRight: '5px'
            }} />
            <span>{connectionStatus}</span>
          </div>
        </div>
        <div className="chat-subtitle">
          我是你的智能助手，随时为你提供帮助
          <button
            onClick={clearChat}
            style={{
              marginLeft: '20px',
              fontSize: '12px',
              background: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '2px 8px',
              cursor: 'pointer'
            }}
          >
            清空对话
          </button>
        </div>
      </div>

      <div className="messages-container">
        <div className="messages-list">
          {messages.length === 0 ? (
            <div className="empty-state">
              <RobotOutlined style={{ fontSize: '48px', color: '#10a37f', marginBottom: '20px' }} />
              <h3>开始对话</h3>
              <p>输入消息开始与AI助手对话</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message-item ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
                >
                  <div className="message-content">
                    {message.type === 'SYSTEM' ? (
                      <div className="system-message" style={{
                        color: '#666',
                        fontStyle: 'italic',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        borderLeft: '3px solid #10a37f'
                      }}>
                        <strong>系统提示：</strong>{message.text}
                      </div>
                    ) : (
                      <>
                        <div className="message-text">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.text}
                          </ReactMarkdown>
                        </div>
                        <div className="message-time">
                          {formatTime(message.timestamp)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
          {isLoading && (
            <div className="message-item ai-message">
              <div className="message-content">
                <div className="loading-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="input-container">
        <div className="input-wrapper">
          <textarea
            ref={chatInputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="输入消息..."
            className="chat-input"
          />
          {inputText.trim() && (
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !isConnected}
              className={`send-button ${inputText.trim() && !isLoading && isConnected ? 'active' : ''}`}
              style={{
                cursor: (!isConnected || isLoading) ? 'not-allowed' : 'pointer',
                opacity: (!isConnected || isLoading) ? 0.5 : 1
              }}
            >
              <SendOutlined />
            </button>
          )}
        </div>
        <div className="input-hint" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span>  按 Enter 发送，Shift + Enter 换行 ｜ NEXTOOK 也可能会犯错，请仔细检查。 </span>
        </div>
      </div>
    </div>
  );
};

export default Home;